import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, TrendingUp, Target, BookOpen, Brain, Zap, Calendar } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { VocabularyWord } from "@/types/vocabulary";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { PageContainer } from "@/components/layout/PageContainer";

const COLORS = {
  unknown: "#ef4444",
  unsure: "#f59e0b",
  known: "#10b981",
};

export default function Analytics() {
  const { userProfile, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      loadVocabulary();
    }
  }, [isAuthenticated, userProfile]);

  const loadVocabulary = async () => {
    if (!userProfile || !db) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, "vocabulary"),
        where("userId", "==", userProfile.uid),
        orderBy("savedAt", "desc")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as VocabularyWord[];
      setWords(data);
    } catch (error) {
      console.error("Error loading vocabulary:", error);
      toast({
        title: "로딩 실패",
        description: "학습 데이터를 불러오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Premium gate
  if (!isAuthenticated || !userProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <TrendingUp className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">로그인이 필요합니다</h2>
            <p className="text-muted-foreground text-center mb-6">
              학습 분석을 보려면 먼저 로그인해주세요.
            </p>
            <Link href="/">
              <Button>홈으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (userProfile.role !== "premium") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Crown className="h-16 w-16 text-yellow-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">프리미엄 전용 기능</h2>
            <p className="text-muted-foreground text-center mb-6">
              학습 분석 대시보드는 프리미엄 회원 전용 기능입니다. 업그레이드하여 학습 진도를 추적하세요!
            </p>
            <Link href="/pricing">
              <Button>프리미엄 가입하기</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">데이터 분석 중...</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalWords = words.length;
  const unknownCount = words.filter(w => w.proficiency === "unknown").length;
  const unsureCount = words.filter(w => w.proficiency === "unsure").length;
  const knownCount = words.filter(w => w.proficiency === "known").length;

  const proficiencyData = [
    { name: "모름", value: unknownCount, color: COLORS.unknown },
    { name: "애매", value: unsureCount, color: COLORS.unsure },
    { name: "앎", value: knownCount, color: COLORS.known },
  ].filter(d => d.value > 0);

  const masteryPercentage = totalWords > 0 ? Math.round((knownCount / totalWords) * 100) : 0;
  const learningPercentage = totalWords > 0 ? Math.round(((knownCount + unsureCount) / totalWords) * 100) : 0;

  // Weekly progress (simulated data for demo)
  const weeklyProgress = [
    { day: "월", words: 3 },
    { day: "화", words: 5 },
    { day: "수", words: 2 },
    { day: "목", words: 7 },
    { day: "금", words: 4 },
    { day: "토", words: 6 },
    { day: "일", words: 8 },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link href="/vocabulary">
          <div>
            <Button variant="ghost" className="mb-6" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              단어장으로
            </Button>
          </div>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">학습 분석</h1>
          <p className="text-muted-foreground">
            나의 학습 진도와 성과를 확인하세요
          </p>
        </div>

        {totalWords === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">분석할 데이터가 없습니다</h2>
              <p className="text-muted-foreground text-center mb-6">
                단어장에 단어를 추가하면 학습 분석을 볼 수 있습니다.
              </p>
              <Link href="/vocabulary">
                <Button>단어장 보기</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">총 단어 수</CardTitle>
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalWords}</div>
                  <p className="text-xs text-muted-foreground mt-1">저장된 단어</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">숙달률</CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{masteryPercentage}%</div>
                  <Progress value={masteryPercentage} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">학습 중</CardTitle>
                  <Brain className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{unsureCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">복습 필요</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">이번 주</CardTitle>
                  <Zap className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">35</div>
                  <p className="text-xs text-muted-foreground mt-1">학습한 단어</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Proficiency Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>숙련도 분포</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={proficiencyData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {proficiencyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-4 mt-4">
                    <Badge variant="outline" className="border-red-500">
                      모름: {unknownCount}
                    </Badge>
                    <Badge variant="outline" className="border-yellow-500">
                      애매: {unsureCount}
                    </Badge>
                    <Badge variant="outline" className="border-green-500">
                      앎: {knownCount}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>주간 학습 활동</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={weeklyProgress}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="words" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Learning Goals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  학습 목표
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">전체 단어 숙달하기</span>
                    <span className="text-sm text-muted-foreground">{knownCount}/{totalWords}</span>
                  </div>
                  <Progress value={masteryPercentage} />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">이번 주 목표 (50단어)</span>
                    <span className="text-sm text-muted-foreground">35/50</span>
                  </div>
                  <Progress value={70} />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">연속 학습 일수</span>
                    <span className="text-sm text-muted-foreground">7일 🔥</span>
                  </div>
                  <Progress value={100} />
                </div>
              </CardContent>
            </Card>

            {/* Learning Tips */}
            <div className="p-4 bg-muted/50 rounded-md border border-border">
              <h3 className="font-semibold mb-2">📊 학습 인사이트</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• 현재 {unknownCount}개의 단어가 "모름" 상태입니다. 플래시카드로 복습해보세요!</li>
                <li>• {unsureCount}개의 단어를 "애매" 상태에서 "앎"으로 올리면 숙달률이 {Math.round(((knownCount + unsureCount) / totalWords) * 100)}%가 됩니다!</li>
                <li>• 이번 주 평균 5개의 단어를 학습하고 있어요. 꾸준히 학습하면 한 달에 150개 이상 마스터할 수 있습니다!</li>
                <li>• 플래시카드로 재미있게 복습하면 학습 효과가 2배입니다.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
